import { useQuery } from '@tanstack/react-query';
import {
  chunk,
  compact,
  concat,
  debounce,
  escapeRegExp,
  isArray,
  uniq,
} from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useGetComponentsWithPagination } from 'src/hooks';
import { ASSEMBLY_ID_CHUNK_SIZE } from 'src/hooks/api/assembly/useGetAllAssemblyComponents';
import { fetchComponentList } from 'src/hooks/api/components/useGetComponents';
import { ComponentTypes, DyProductSource, IComponent } from 'src/interfaces';
import { queryKeys } from 'src/utils';

import { filterDyComponents } from './ComponentList.Modal.helper';

export const useFilteredComponents = (
  filters: string[] = [],
  enableDyFilter?: boolean,
  suggestedComponentTypes: (ComponentTypes | DyProductSource)[] = [],
  enabled: boolean = true,
  noEndDate: boolean = true,
  useSimpleSearch: boolean = false,
) => {
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedComponent, setSelectedComponent] = useState<IComponent>();
  const inputRef = useRef<string>('');

  const searchQueryParams = useMemo(() => {
    const termsToSearch = useSimpleSearch
      ? searchTerms.length > 0
        ? searchTerms
        : inputValue.trim()
        ? [inputValue]
        : []
      : searchTerms.length > 0
      ? searchTerms
      : [];

    if (termsToSearch.length === 0) {
      return [];
    }

    const escapedTerms = compact(
      termsToSearch.map((term) => escapeRegExp(term.trim())),
    );
    if (escapedTerms.length === 0) {
      return [];
    }
    const orPattern = escapedTerms.join('|');

    return [
      `name.regexp=${encodeURIComponent(orPattern)}`,
      'with_component_tag_name=true',
    ];
  }, [searchTerms, inputValue, useSimpleSearch]);

  const {
    data: componentsData,
    isLoading: isLoadingComponents,
    isFetchingNextPage,
    error: errorLoadingComponents,
    hasNextPage,
    fetchNextPage,
    isStale,
  } = useGetComponentsWithPagination(
    concat(filters, [
      'status=ready',
      `sort=-created_at`,
      noEndDate ? `end_date.null=true` : '',
      ...searchQueryParams,
    ]),
    { enabled: enabled },
  );
  const isQueryLoading = enabled ? isLoadingComponents : false;

  const getNextComponentPage = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        inputRef.current = value;
        setInputValue(value);
      }, 500),
    [],
  );

  const searchComponents = (value: string | string[]) => {
    const stringValue = isArray(value) ? value.join(' ') : value;
    inputRef.current = stringValue;
    debouncedSearch(stringValue);
  };

  const addSearchTerm = (term: string) => {
    const trimmedTerm = term.trim();
    if (trimmedTerm && !searchTerms.includes(trimmedTerm)) {
      setSearchTerms((prev) => uniq([...prev, trimmedTerm]));
      setInputValue('');
    }
  };

  const removeSearchTerm = (term: string) => {
    setSearchTerms((prev) => prev.filter((t) => t !== term));
  };

  const onSelectedComponent = (component: IComponent) => {
    setSelectedComponent(component);
  };

  const filteredComponentsData = useMemo(() => {
    return enableDyFilter
      ? filterDyComponents(componentsData, suggestedComponentTypes)
      : componentsData;
  }, [componentsData, enableDyFilter, suggestedComponentTypes]);

  return {
    componentsData: filteredComponentsData,
    isLoadingComponents: isQueryLoading,
    isFetchingNextPage,
    errorLoadingComponents,
    getNextComponentPage,
    hasNextPage,
    selectedComponent,
    onSelectedComponent,
    setSelectedComponent,
    searchComponents,
    inputValue,
    searchTerms,
    addSearchTerm,
    removeSearchTerm,
    isComponentsDataStaled: isStale,
  };
};

// Chunked variant for the bulk-replace "from" picker. Takes component IDs
// directly (not as a `id.list=...` filter string) and chunks the request to
// avoid the 8KB URL overflow that breaks at large selections.
// Returns the same shape as `useFilteredComponents` so the modal renders
// identically; pagination is collapsed into a single page because the result
// set is already bounded by the caller's selection.
export const useFilteredComponentsByIds = (
  componentIds: number[],
  enabled: boolean = true,
  noEndDate: boolean = true,
  filters: string[] = [],
) => {
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedComponent, setSelectedComponent] = useState<IComponent>();
  const inputRef = useRef<string>('');

  const sortedIds = useMemo(
    () => [...componentIds].sort((a, b) => a - b),
    [componentIds],
  );

  const searchQueryParams = useMemo(() => {
    const termsToSearch =
      searchTerms.length > 0
        ? searchTerms
        : inputValue.trim()
        ? [inputValue]
        : [];

    if (termsToSearch.length === 0) {
      return [];
    }

    const escapedTerms = compact(
      termsToSearch.map((term) => escapeRegExp(term.trim())),
    );
    if (escapedTerms.length === 0) {
      return [];
    }
    const orPattern = escapedTerms.join('|');

    return [
      `name.regexp=${encodeURIComponent(orPattern)}`,
      'with_component_tag_name=true',
    ];
  }, [searchTerms, inputValue]);

  const baseFilters = useMemo(
    () =>
      compact([
        'status=ready',
        'sort=-created_at',
        noEndDate ? 'end_date.null=true' : '',
        ...filters,
        ...searchQueryParams,
      ]),
    [noEndDate, filters, searchQueryParams],
  );

  const {
    data: rawComponents,
    isLoading: isLoadingComponents,
    error: errorLoadingComponents,
    isStale,
  } = useQuery({
    queryKey: queryKeys.componentListing.all([
      `id.list=${sortedIds.join('|')}`,
      ...baseFilters,
    ]),
    queryFn: async () => {
      if (sortedIds.length === 0) {
        return [] as IComponent[];
      }
      const idChunks = chunk(sortedIds, ASSEMBLY_ID_CHUNK_SIZE);
      const responses = await Promise.all(
        idChunks.map(async (c) => {
          const results: IComponent[] = [];
          let page = 1;
          // The endpoint returns at most 10 items per page by default.
          // Using count=true (isPagination=true) conflicts with id.list= filtering,
          // so we paginate without it and stop when a page returns no results.
          const maxPages = Math.ceil(c.length / 10) + 1;

          while (page <= maxPages) {
            const batch = (await fetchComponentList(
              [`id.list=${c.join('|')}`, `page=${page}`, ...baseFilters],
              false,
            )) as IComponent[];

            if (!batch?.length) break;
            results.push(...batch);
            page++;
          }

          return results;
        }),
      );

      return responses.flat() as IComponent[];
    },
    enabled: enabled && sortedIds.length > 0,
    staleTime: Infinity,
  });

  const componentsData = useMemo(
    () =>
      rawComponents
        ? {
            pages: [
              {
                data: rawComponents,
                totalPages: 1,
                totalCount: rawComponents.length,
              },
            ],
          }
        : undefined,
    [rawComponents],
  );

  const isQueryLoading = enabled ? isLoadingComponents : false;

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        inputRef.current = value;
        setInputValue(value);
      }, 500),
    [],
  );

  const searchComponents = (value: string | string[]) => {
    const stringValue = isArray(value) ? value.join(' ') : value;
    inputRef.current = stringValue;
    debouncedSearch(stringValue);
  };

  const addSearchTerm = (term: string) => {
    const trimmedTerm = term.trim();
    if (trimmedTerm && !searchTerms.includes(trimmedTerm)) {
      setSearchTerms((prev) => uniq([...prev, trimmedTerm]));
      setInputValue('');
    }
  };

  const removeSearchTerm = (term: string) => {
    setSearchTerms((prev) => prev.filter((t) => t !== term));
  };

  const onSelectedComponent = (component: IComponent) => {
    setSelectedComponent(component);
  };

  return {
    componentsData,
    isLoadingComponents: isQueryLoading,
    isFetchingNextPage: false,
    errorLoadingComponents,
    getNextComponentPage: () => {},
    hasNextPage: false,
    selectedComponent,
    onSelectedComponent,
    setSelectedComponent,
    searchComponents,
    inputValue,
    searchTerms,
    addSearchTerm,
    removeSearchTerm,
    isComponentsDataStaled: isStale,
  };
};
